// src/js/config/configFormHandler.js

import { DOMElements } from './dom.js';
import { DependencyManager } from './dependencyManager.js';
import { PricingCalculator } from './pricingCalculator.js';

/**
 * Justerar värdet i ett nummer-inputfält när +/- knapparna klickas.
 * @param {HTMLElement} button - Knappen som klickades.
 * @param {number} delta - Värdeändringen (+1 eller -1).
 */
export function adjustNumber(button, delta) {
    const input = button.closest('td').querySelector('input[type="number"]');
    if (!input) return;

    const step = parseFloat(input.dataset.step) || 1;
    const currentValue = parseFloat(input.value) || 0;
    const newValue = currentValue + (delta * step);

    if (newValue >= 0) {
        // Använder toFixed för att hantera decimaler korrekt
        input.value = Number(newValue.toFixed(2));
    }
}

/**
 * Samlar in all data från formuläret till ett konfigurationsobjekt.
 * @returns {Object} Ett komplett konfigurationsobjekt.
 */
export function collectFormData() {
    const { configNameInput, pricingTableRows } = DOMElements;
    const configName = configNameInput ? configNameInput.value : '';

    const cheapestTier = PricingCalculator.getCurrentCheapestTier();
    const summaryForCheapestTier = PricingCalculator.getCurrentTierSummaries();

    let configType = 'standard';
    if (window.SimP_Page_Context && window.SimP_Page_Context.is_flex_page === '1') {
        configType = 'flex';
    }
    
    const selectedFeatures = {};
    const formRows = Array.from(pricingTableRows);
    
    formRows.forEach(row => {
        const featureId = row.dataset.featureId;
        const type = row.dataset.type;

        if (!featureId || !type) return;

        if (type === "single") {
            const checkbox = row.querySelector("input[type='checkbox']");
            if (checkbox && checkbox.checked) {
                selectedFeatures[featureId] = true;
            }
        } else if (type === "multi") {
            const qtyInput = row.querySelector("input[type='number']");
            const value = qtyInput ? parseFloat(qtyInput.value) : 0;
            if (qtyInput && value > 0) {
                selectedFeatures[featureId] = value;
            }
        }
    });

    return {
        name: configName,
        timestamp: new Date().toISOString(),
        type: configType,
        cheapestTier: cheapestTier,
        summaryPricesForCheapestTier: summaryForCheapestTier,
        features: selectedFeatures
    };
}

/**
 * Fyller i formuläret baserat på en vald paket-nivå (t.ex. 'Budget', 'Pro').
 * @param {string} tier - Namnet på paketet som ska appliceras.
 */
export function applyTier(tier) {
    const { pricingTableRows } = DOMElements;
    pricingTableRows.forEach(row => {
        if (row.classList.contains('is-disabled')) return;
        
        const type = row.dataset.type;
        if (type === "single") {
            const checkbox = row.querySelector("input[type='checkbox']");
            const cell = row.querySelector(`[data-category='${tier}']`);
            if (checkbox && cell) {
                checkbox.checked = cell.textContent.trim() === '✔';
            }
        } else if (type === "multi") {
            const qtyInput = row.querySelector("input[type='number']");
            const included = parseFloat(row.getAttribute(`data-included${tier.toLowerCase()}`)) || 0;
            if (qtyInput) {
                qtyInput.value = included;
            }
        }
    });

    // Efter att ha ändrat värden, måste vi uppdatera priserna.
    PricingCalculator.updatePrices();
}

/**
 * Återställer alla val i formuläret till 0 eller false.
 */
export function resetSelection() {
    const { pricingTableRows } = DOMElements;
    pricingTableRows.forEach(row => {
        const checkbox = row.querySelector("input[type='checkbox']");
        if (checkbox) checkbox.checked = false;
        
        const qtyInput = row.querySelector("input[type='number']");
        if (qtyInput) qtyInput.value = 0;
    });

    console.log("Formuläret har återställts.");
    PricingCalculator.updatePrices();
}

/**
 * Laddar ett konfigurationsobjekt och fyller i formulärets fält.
 */
export function loadConfigurationIntoForm(configObject) {
    const { configNameInput, pricingTableRows } = DOMElements;
    let wasConverted = false; // Flagga för att se om vi konverterar

    const isFlexPage = window.SimP_Page_Context?.is_flex_page === '1';
    const configType = configObject.type || 'standard';

    if (configType === 'flex' && !isFlexPage) {
        wasConverted = true; // Sätt flaggan till true
        // INGET showMessage-anrop här längre
    }

    resetSelection();

    if (configNameInput && configObject.name) {
        configNameInput.value = configObject.name;
    }

    const features = configObject.features || {};
    for (const featureId in features) {
        const row = Array.from(pricingTableRows).find(r => r.dataset.featureId === featureId);
        if (row) {
            // Denna logik (som kräver data-is-flex) behövs inte längre om vi inte laddar in värdena alls.
            // Vi förenklar: om raden inte finns på standardsidan kommer inget värde att sättas.
            const featureValue = features[featureId];
            const type = row.dataset.type;
            if (type === "single") {
                const checkbox = row.querySelector("input[type='checkbox']");
                if (checkbox && !checkbox.disabled) checkbox.checked = !!featureValue;
            } else if (type === "multi") {
                const qtyInput = row.querySelector("input[type='number']");
                if (qtyInput && !qtyInput.disabled) qtyInput.value = parseFloat(featureValue) || 0;
            }
        }
    }

    DependencyManager.evaluateDependencies();
    PricingCalculator.updatePrices();

    return wasConverted; // Returnera flaggan
}